import * as React from "react";
import { makeStyles, createStyles, Theme, Grid, Typography } from "@material-ui/core";
import ReactMarkdown from "react-markdown";
import Section from "../Section";
import {
  SECTION_NAVY,
  SECTION_PANEL_NAVY,
  SECTION_PADDING_Y,
} from "../../styles/homepageSections";

export type ImpactMetricData = {
  value: string;
  title: string;
  source?: string | null;
};

export type BusinessImpactData = {
  subheadline?: string | null;
  headline?: string | null;
  description?: string | null;
  metrics?: ImpactMetricData[] | null;
  note?: string | null;
};

const hasText = (value?: string | null) =>
  Boolean(value && value.replace(/<[^>]*>/g, "").trim());

const markdownComponents = {
  p: ({ children }: { children?: React.ReactNode }) => (
    <p style={{ margin: "0 0 1em" }}>{children}</p>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong>{children}</strong>
  ),
};

const useStyles = makeStyles((t: Theme) =>
  createStyles({
    section: {
      padding: SECTION_PADDING_Y.mobile,
      color: "#fff",
      [t.breakpoints.up("md")]: {
        padding: SECTION_PADDING_Y.desktop,
      },
    },
    header: {
      maxWidth: 720,
      marginBottom: "2.5rem",
      [t.breakpoints.up("md")]: {
        marginBottom: "3rem",
      },
    },
    subheadline: {
      color: t.palette.primary.main,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      marginBottom: 12,
      [t.breakpoints.up("md")]: {
        fontSize: 13,
        marginBottom: 16,
      },
    },
    headline: {
      color: "#fff",
      fontWeight: 600,
      fontSize: 32,
      lineHeight: 1.25,
      margin: 0,
      padding: "0 0 2rem !important",
      [t.breakpoints.up("md")]: {
        fontSize: 44,
        lineHeight: 1.2,
      },
    },
    headlineAfterSubheadline: {
      paddingTop: "0 !important",
      marginTop: 0,
    },
    description: {
      color: "rgba(255, 255, 255, 0.75)",
      fontSize: 18,
      lineHeight: 1.55,
      "& p:last-child": {
        marginBottom: 0,
      },
      [t.breakpoints.up("md")]: {
        fontSize: 20,
        lineHeight: 1.6,
      },
    },
    metricsPanel: {
      backgroundColor: SECTION_PANEL_NAVY,
      borderRadius: 12,
      padding: "32px 24px",
      [t.breakpoints.up("md")]: {
        padding: "40px 32px",
      },
    },
    metricCell: {
      display: "flex",
      flexDirection: "column",
      minHeight: 200,
      padding: "8px 0",
      [t.breakpoints.up("md")]: {
        minHeight: 220,
        padding: "8px 16px",
      },
    },
    metricCellBorder: {
      [t.breakpoints.up("md")]: {
        borderRight: "1px solid rgba(255, 255, 255, 0.12)",
      },
    },
    metricValue: {
      color: t.palette.primary.main,
      fontWeight: 600,
      fontSize: 40,
      lineHeight: 1.1,
      marginBottom: 16,
      [t.breakpoints.up("md")]: {
        fontSize: 48,
      },
    },
    metricTitle: {
      color: "#fff",
      fontWeight: 600,
      fontSize: 18,
      lineHeight: 1.4,
      flex: 1,
      marginBottom: 24,
      [t.breakpoints.up("md")]: {
        fontSize: 20,
      },
    },
    metricSource: {
      color: "rgba(255, 255, 255, 0.5)",
      fontSize: 14,
      fontStyle: "italic",
      lineHeight: 1.4,
      marginTop: "auto",
    },
    note: {
      textAlign: "center",
      color: "rgba(255, 255, 255, 0.55)",
      fontSize: 15,
      lineHeight: 1.5,
      maxWidth: 900,
      margin: "2rem auto 0",
      "& p": {
        margin: 0,
      },
      [t.breakpoints.up("md")]: {
        fontSize: 16,
        marginTop: "2.5rem",
      },
    },
  })
);

const BusinessImpactSection: React.FC<{ data?: BusinessImpactData | null }> = ({
  data,
}) => {
  const classes = useStyles();

  if (!data) {
    return null;
  }

  const metrics =
    data.metrics?.filter((m) => m?.value && m?.title) ?? [];
  const hasCopy =
    hasText(data.subheadline) ||
    hasText(data.headline) ||
    hasText(data.description);
  const hasNote = hasText(data.note);

  if (!hasCopy && metrics.length === 0 && !hasNote) {
    return null;
  }

  return (
    <Section color={SECTION_NAVY} className={classes.section}>
      {hasCopy && (
        <div className={classes.header}>
          {hasText(data.subheadline) && (
            <Typography component="p" className={classes.subheadline}>
              {data.subheadline}
            </Typography>
          )}
          {hasText(data.headline) && (
            <Typography
              variant="h2"
              component="h2"
              className={[
                classes.headline,
                hasText(data.subheadline) && classes.headlineAfterSubheadline,
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {data.headline}
            </Typography>
          )}
          {hasText(data.description) && (
            <div className={classes.description}>
              <ReactMarkdown linkTarget="_blank" components={markdownComponents}>
                {data.description!}
              </ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {metrics.length > 0 && (
        <div className={classes.metricsPanel}>
          <Grid container>
            {metrics.map((metric, index) => {
              const showBorder =
                metrics.length > 1 &&
                index !== metrics.length - 1 &&
                (index + 1) % 4 !== 0;

              return (
                <Grid item xs={12} sm={6} md={3} key={`${metric.value}-${index}`}>
                  <div
                    className={[
                      classes.metricCell,
                      showBorder && classes.metricCellBorder,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <Typography component="p" className={classes.metricValue}>
                      {metric.value}
                    </Typography>
                    <Typography component="p" className={classes.metricTitle}>
                      {metric.title}
                    </Typography>
                    {hasText(metric.source) && (
                      <Typography component="p" className={classes.metricSource}>
                        {metric.source}
                      </Typography>
                    )}
                  </div>
                </Grid>
              );
            })}
          </Grid>
        </div>
      )}

      {hasNote && (
        <div className={classes.note}>
          <ReactMarkdown linkTarget="_blank" components={markdownComponents}>
            {data.note!}
          </ReactMarkdown>
        </div>
      )}
    </Section>
  );
};

export default BusinessImpactSection;
